import React, { useMemo, useEffect } from 'react';
import BaseMap from './BaseMap';
import { scaleThreshold } from 'd3-scale';
import { Box } from '@mui/material';
import { formatPercentage } from '../utils/mapUtils';
import { fipsToStateName } from '../utils/fipsUtils';

const LowIncomeTractsMap = ({ data }) => {
  // Debug on mount
  useEffect(() => {
    console.log('LowIncomeTractsMap data received:', data?.length || 0, 'items');
    console.log('Low income tracts count:', 
      data?.filter(d => d.lowIncomeTracts === 1)?.length || 0);
    
    // Log some examples of counties with low income tracts
    const examples = data?.filter(d => d.lowIncomeTracts === 1)?.slice(0, 5) || [];
    if (examples.length > 0) {
      console.log('Examples of low income counties:', 
        examples.map(d => `${d.countyName}, ${d.stateCode}`));
    }
  }, [data]);

  // Convert data to a consistent format with normalized FIPS codes
  const processedData = useMemo(() => {
    return data?.map(item => ({
      fips: String(item.fips).padStart(5, '0'),
      lowIncomeTracts: item.lowIncomeTracts,
      countyName: item.countyName,
      stateCode: item.stateCode
    })) || [];
  }, [data]);

  // Binary color scale - either low income or not with darker gray
  const colorScale = useMemo(() => 
    scaleThreshold()
      .domain([0.5])  // Binary threshold
      .range(['#AAAAAA', '#6A51A3']), // Darker grey for 0, purple for 1
  []);

  // Update getFillColor to use direct matching first, then fallback
  const getFillColor = (geo, directMatch = null) => {
    // If we already have a direct match from BaseMap's matching logic, use it
    if (directMatch) {
      return directMatch.lowIncomeTracts > 0
        ? colorScale(1)
        : colorScale(0);
    }
    
    // Otherwise, do the matching here
    const fips = String(geo.id).padStart(5, '0');
    const countyData = processedData.find(d => d.fips === fips);
    
    if (countyData && countyData.lowIncomeTracts === 1) {
      console.log(`Found low income tract for ${geo.properties?.name || 'Unknown'}, FIPS: ${fips}`);
    }
    
    return countyData && countyData.lowIncomeTracts > 0
      ? colorScale(1)
      : colorScale(0);
  };

  // Create legend data
  const legendColors = useMemo(() => 
    ['#AAAAAA', '#6A51A3'],
  []);

  const legendValues = useMemo(() => [
    'Not Low Income',
    'Low Income Area'
  ], []);

  // Update handleMouseMove to use fipsToStateName
  const handleMouseMove = (event, geo, countyData) => {
    const countyName = geo.properties?.name || 'Unknown';
    const stateFips = geo.id ? String(geo.id).substring(0, 2) : '';
    const stateName = fipsToStateName[stateFips] || '';
    
    const isLowIncome = countyData?.lowIncomeTracts > 0;
    
    return `
      <strong>${countyName} County${stateName ? `, ${stateName}` : ''}</strong><br/>
      Low Income Area: ${isLowIncome ? 'Yes' : 'No'}
    `;
  };

  // Add data validation
  useEffect(() => {
    console.log('[LowIncomeTractsMap] Processed data sample:', {
      inputFips: data?.[0]?.fips,
      processedFips: processedData?.[0]?.fips,
      geoFipsExample: "46121",
      match: processedData?.some(d => d.fips === "46121"),
      lowIncomeCount: processedData?.filter(d => d.lowIncomeTracts > 0).length
    });
  }, [processedData]);

  return (
    <BaseMap
      data={data}
      getFillColor={getFillColor}
      onHover={(e, geo, countyData) => handleMouseMove(e, geo, countyData)}
      legendTitle="Low Income Areas"
      legendColors={legendColors}
      legendValues={legendValues}
    />
  );
};

export default LowIncomeTractsMap;